import { getPlatformHint, hasMaxLaunchParams, isMax, PLATFORMS } from './detect';

export const PROVIDER = 'max';

const MAX_SDK_URL = 'https://st.max.ru/js/max-web-app.js';

let sdkLoadPromise = null;

function shouldLoadMaxSdk() {
  return getPlatformHint() === PLATFORMS.MAX || hasMaxLaunchParams() || isMax();
}

export function loadMaxSdk() {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if (!shouldLoadMaxSdk()) {
    return Promise.resolve();
  }
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${MAX_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('MAX Bridge failed to load')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = MAX_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('MAX Bridge failed to load'));
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

function getWebApp() {
  return window.WebApp || null;
}

export function isAvailable() {
  if (!isMax()) {
    return false;
  }
  const webApp = getWebApp();
  return Boolean(webApp?.initData || webApp?.initDataUnsafe?.user || webApp?.platform);
}

export async function ensureReady() {
  await loadMaxSdk();
  return isAvailable() ? getWebApp() : null;
}

export function init() {
  const webApp = getWebApp();
  if (!webApp) return null;

  webApp.ready?.();
  webApp.expand?.();

  return webApp;
}

export function getAuthPayload() {
  const webApp = getWebApp();
  const fromBridge = webApp?.initData || null;
  const fromUrl = readInitDataFromUrl();

  if (fromBridge && initDataContainsUser(fromBridge)) {
    return fromBridge;
  }
  if (fromUrl && initDataContainsUser(fromUrl)) {
    return fromUrl;
  }

  return fromBridge || fromUrl || null;
}

function initDataContainsUser(initData) {
  return /(?:^|&)user=/.test(initData);
}

function readInitDataFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  const sources = [
    window.location.hash?.slice(1) || '',
    window.location.search?.slice(1) || '',
  ];

  for (const source of sources) {
    if (!source) {
      continue;
    }

    const params = new URLSearchParams(source);
    const webAppData = params.get('WebAppData') || params.get('webAppData');
    if (!webAppData) {
      continue;
    }

    let decoded = webAppData;
    for (let i = 0; i < 4; i += 1) {
      const next = decodeURIComponent(decoded);
      if (next === decoded) {
        break;
      }
      decoded = next;
    }
    if (decoded) {
      return decoded;
    }
  }

  return null;
}

export function hasAuthPayload() {
  const payload = getAuthPayload();
  if (payload && initDataContainsUser(payload)) {
    return true;
  }

  const user = getWebApp()?.initDataUnsafe?.user;
  return Boolean(user?.id);
}

function readStartParamFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('startapp')) {
    return searchParams.get('startapp');
  }

  const hash = window.location.hash?.slice(1) || '';
  if (!hash) {
    return null;
  }

  const hashParams = new URLSearchParams(hash);
  if (hashParams.has('startapp')) {
    return hashParams.get('startapp');
  }

  return null;
}

export function getStartParam() {
  const webApp = getWebApp();

  if (webApp?.initDataUnsafe?.start_param) {
    return webApp.initDataUnsafe.start_param;
  }

  const fromBridgeInitData = readStartParamFromInitData(webApp?.initData);
  if (fromBridgeInitData) {
    return fromBridgeInitData;
  }

  const fromUrlInitData = readStartParamFromInitData(readInitDataFromUrl());
  if (fromUrlInitData) {
    return fromUrlInitData;
  }

  return readStartParamFromUrl();
}

function readStartParamFromInitData(initData) {
  if (!initData) {
    return null;
  }
  try {
    return new URLSearchParams(initData).get('start_param');
  } catch {
    return null;
  }
}

export function getUnavailableMessage() {
  return 'Данные авторизации MAX недоступны';
}

export function openApp(url) {
  const webApp = getWebApp();
  if (webApp?.openLink) {
    webApp.openLink(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
